/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * TopBarShell — 44px horizontal tab bar at the top of the sidebar area,
 * with each module as a tab (icon + name). Active tab has a colored underline.
 * 210px sidebar below the tabs.
 */

import { AppVersion } from '@/components/ui/AppVersion';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { useTheme } from '@/lib/theme';
import { useModuleLogos } from '@/stores/layoutStore';
import { projectConfig } from '@/config/project.config';
import type { ModuleShellProps } from './types';

export function TopBarShell({ modules, activeModule, onModuleChange, children }: ModuleShellProps) {
  const { t } = useTranslation('common');
  const { isDark } = useTheme();
  const showLogos = useModuleLogos();
  const logo = isDark ? activeModule.logos.dark : activeModule.logos.light;

  return (
    <aside className="ms-topbar-sidebar">
      {/* Tab bar */}
      <div className="ms-topbar-tabs">
        {modules.map((mod) => {
          const isActive = mod.id === activeModule.id;
          const Icon = mod.icon;
          const modLogo = isDark ? mod.logos.dark : mod.logos.light;
          return (
            <button
              key={mod.id}
              className={`ms-topbar-tab ${isActive ? 'active' : ''}`}
              onClick={() => { if (!isActive) onModuleChange(mod); }}
              title={t(mod.nameKey)}
              type="button"
              style={isActive ? {
                '--tab-color': mod.color,
              } as React.CSSProperties : undefined}
            >
              {showLogos ? (
                <img src={modLogo} alt={t(mod.nameKey)} className="ms-topbar-tab-logo" />
              ) : (
                <Icon size={14} />
              )}
              <span className="ms-topbar-tab-label">{mod.shortName}</span>
              {isActive && (
                <div className="ms-topbar-tab-indicator" style={{ background: mod.color }} />
              )}
            </button>
          );
        })}
      </div>

      {/* Header */}
      <div className="ms-topbar-header">
        <div className="ms-topbar-brand">
          <div className="ms-topbar-logo">
            <img src={logo} alt={t(activeModule.nameKey)} />
          </div>
          <div>
            <div className="ms-topbar-title">{t(activeModule.nameKey)}</div>
            <div className="ms-topbar-subtitle">{t(activeModule.subtitleKey)}</div>
          </div>
        </div>
      </div>

      {/* Nav content */}
      <div className="ms-topbar-nav">{children}</div>

      {/* Footer — legal/copyright only */}
      <div className="ms-topbar-footer">
        <div className="ms-topbar-legal">
          <span>&copy; 2026 CoreX-Suite</span>
          <span className="ms-topbar-legal-sep">&middot;</span>
          <NavLink to="/privacy" className="ms-topbar-legal-link">{t('core:sidebar.privacy', 'Privacy')}</NavLink>
          <span className="ms-topbar-legal-sep">&middot;</span>
          <NavLink to="/terms" className="ms-topbar-legal-link">{t('core:sidebar.terms', 'Terms')}</NavLink>
          <span className="ms-topbar-legal-sep">&middot;</span>
          <AppVersion />
        </div>
      </div>

      <style>{styles}</style>
    </aside>
  );
}

const styles = `
  .ms-topbar-sidebar {
    --module-shell-width: 210px;
    position: fixed;
    top: 0;
    left: 0;
    width: 210px;
    height: 100vh;
    background: var(--color-bg-secondary);
    border-right: 1px solid var(--color-border-subtle);
    display: flex;
    flex-direction: column;
    z-index: 100;
  }

  /* Tab bar */
  .ms-topbar-tabs {
    display: flex;
    border-bottom: 1px solid var(--color-border-subtle);
    flex-shrink: 0;
    overflow-x: auto;
    scrollbar-width: none;
  }

  .ms-topbar-tabs::-webkit-scrollbar {
    display: none;
  }

  .ms-topbar-tab {
    position: relative;
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2px;
    padding: 8px 4px 10px;
    border: none;
    background: transparent;
    color: var(--color-text-muted);
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .ms-topbar-tab:hover {
    color: var(--color-text-primary);
    background: var(--color-bg-hover);
  }

  .ms-topbar-tab.active {
    color: var(--tab-color, var(--color-brand));
  }

  .ms-topbar-tab-logo {
    width: 16px;
    height: 16px;
    object-fit: contain;
    border-radius: 2px;
  }

  .ms-topbar-tab-label {
    font-size: 8px;
    font-weight: 600;
    letter-spacing: 0.03em;
    line-height: 1;
  }

  .ms-topbar-tab-indicator {
    position: absolute;
    bottom: 0;
    left: 20%;
    right: 20%;
    height: 2px;
    border-radius: 2px 2px 0 0;
  }

  /* Header */
  .ms-topbar-header {
    padding: 10px 12px;
    border-bottom: 1px solid var(--color-border-subtle);
    flex-shrink: 0;
  }

  .ms-topbar-brand {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .ms-topbar-logo {
    width: 28px;
    height: 28px;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .ms-topbar-logo img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  .ms-topbar-title {
    font-family: var(--font-heading);
    font-size: 12px;
    font-weight: 600;
    color: var(--color-text-primary);
    line-height: 1.2;
  }

  .ms-topbar-subtitle {
    font-size: 10px;
    color: var(--color-text-secondary);
  }

  .ms-topbar-nav {
    flex: 1;
    overflow-y: auto;
    padding: 4px 6px;
  }

  .ms-topbar-footer {
    border-top: 1px solid var(--color-border-subtle);
    padding: 0;
    flex-shrink: 0;
  }

  .ms-topbar-legal {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    padding: 6px 12px;
    font-size: 0.6rem;
    color: var(--color-text-muted);
  }

  .ms-topbar-legal-sep {
    margin: 0 4px;
    color: var(--color-border-subtle);
  }

  .ms-topbar-legal-link {
    color: var(--color-text-muted);
    text-decoration: none;
    font-size: 0.6rem;
  }

  .ms-topbar-legal-link:hover {
    color: var(--color-text-secondary);
  }

  @media (max-width: 768px) {
    .ms-topbar-sidebar {
      transform: translateX(-100%);
      transition: transform 0.3s ease;
    }
  }
`;
